import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Stepper,
  StepLabel,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Paper,
  Step,
  Divider,
} from "@mui/material";

import LessonCard from "./LessonCard";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import LoadingDialog from "./LoadingDialog";
import PrintIcon from "@mui/icons-material/Print";
import { BackButton } from "./functions";
import FeaturedLessons from "./FeaturedLessons";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import MoreToCome from "./MoreToCome";
import WelcomeDialogLessons from "./WelcomeDialogLessons";
import HistoryIcon from "@mui/icons-material/History";
import ClassIcon from "@mui/icons-material/Class";
import SlideInPaper from "./SlideInPaper";
import ShareLink from "./ShareLink";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ImageLoadingDialog from "./ImageLoadingDialog";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AddonTimelineSimple from "./AddonTimelineSimple";
import TimelineDisplay from "./TimelineDisplay";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import Translate from "./Translate";
import AddonTimeline from "./AddonTimeline";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import { useParams } from "react-router-dom";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonIcon from "@mui/icons-material/Person";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import TimelineSimpleDisplay from "./TimelineSimpleDisplay";
import AddIcon from "@mui/icons-material/Add";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import LessonQuizDisplay from "./LessonQuizDisplay";
import AddonQuiz from "./AddonQuiz";
import ReadingComprehensionDialog from "./ReadingComprehensionDialog";
import DashedPapers from "./DashedPapers";
import LessonExercisesDisplay from "./LessonExercisesDisplay";
import ImageIcon from "@mui/icons-material/Image";
import ImageGenerationModal from "./ImageGenerationModal";
import HoverableImage from "./HoverableImage";
import { callApi } from "./api";
import { db } from "./firebase";
import {
  collection,
  query,
  where,
  limit,
  addDoc,
  updateDoc,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";

import Logger from "./Logger";
import LanguageSelector from "./LanguageSelector";

import { getThemes } from "./additionalThemes/DiaryLesson";
import { ThemeSelectorFlow } from "./ThemeSelectorFlow";
import InfoBox from "./InfoBox";
import { handlePrintLiteratureLesson } from "./printFunctions";

import { generateAndProcessImage } from "./functions";
import ShareDialog from "./ShareDialog";
import ShareIcon from "@mui/icons-material/Share";
import LoadingBackdrop from "./LoadingBackdrop";
import { useSnackbar } from "./SnackbarHandler";
import ProgressBar from "./ProgressBar";
import LessonDrawer from "./LessonDrawer";
import SaveLessonDialog from "./SaveLessonDialog";
import EditIcon from "@mui/icons-material/Edit";
import { DashedPaper } from "../StyledComponents";
import { getStyles } from "../index";
import { trackLesson, handleImageGenerationError } from "./functions";
import GradeSelector, { gradeInstructions } from "./GradeSelector";
import { useTheme } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";

import DiaryContent from "./DiaryContent";

export const LESSON_DATA = {
  inledningar: {
    title: "Inledningar",
    description: "Tidlösa inledningar i svensk prosa",
    image: "https://sportkuriren.se/wp-content/uploads/inledning.jpg",
    books: [
      {
        title: "Röda rummet",
        author: "August Strindberg",
        pdfLink: "/pdfs/roda-rummet-inledningar.pdf",
        cover: "https://sportkuriren.se/wp-content/uploads/strindberg.jpg",
        type: "Inledning",
        about:
          "“Röda rummet” är en av Sveriges mest inflytelserika romaner, skriven av August Strindberg och publicerad 1879. Den betraktas ofta som den första moderna svenska romanen och utgör ett nyckelverk i svensk litteraturhistoria. Romanen ger en skarp och satirisk bild av Stockholmssamhället under 1870-talet och riktar kritik mot samtidens politik, kultur och samhällsinstitutioner.",
        previewText: "Det var en afton i början av maj...",
        fullText:
          "Det var en afton i början av maj. Den lilla trädgården på Mosebacke hade ännu icke blivit öppnad för allmänheten och rabatterna voro ej uppgrävda; snödropparne hade arbetat sig upp genom fjolårets lövsamlingar och höllo just på att sluta sin korta verksamhet för att lämna plats åt de ömtåligare saffransblommorna, vilka tagit skydd under ett ofruktsamt päronträd; syrenerna väntade på sydlig vind för att få gå i blom, men lindarne bjödo ännu kärleksfilter i sina obrustna knoppar åt bofinkarne, som börjat bygga sina lavklädda bon mellan stam och gren; ännu hade ingen mänskofot trampat sandgångarne sedan sista vinterns snö gått bort och därför levdes ett obesvärat liv därinne av både djur och blommor. Gråsparvarne höllo på att samla upp skräp, som de sedan gömde under takpannorna på navigationsskolans hus; de drogos om spillror av rakethylsor från sista höstfyrverkeriet, de plockade halmen från unga träd som året förut sluppit ur skolan på Rosendal - och allting sågo de! De hittade barège-lappar i bersåer och kunde mellan stickorna på en bänkfot draga fram hårtappar efter hundar, som icke slagits där sedan Josefinadagen i fjor. Där var ett liv och ett kiv.\n\nMen solen stod över Liljeholmen och sköt hela kvastar av strålar mot öster; de gingo genom rökarne från Bergsund, de ilade fram över Riddarfjärden, klättrade upp till korset på Riddarholmskyrkan, kastade sig över till Tyskans branta tak, lekte med vimplarne på Skeppsbrobåtarne, illuminerade i fönstren på Stora Sjötullen, eklärerade Lidingöskogarne och tonade bort i ett rosenfärgat moln, långt, långt ut i fjärran, där havet ligger. Och därifrån kom vinden, och hon gjorde samma färd tillbaka, genom Vaxholm, förbi fästningen, förbi Sjötulln, utmed Siklaön, gick in bakom Hästholmen och tittade på sommarnöjena; ut igen, fortsatte och kom in i Danviken, blev skrämd och rusade av utmed södra stranden, kände lukten av kol, tjära och tran, törnade mot Stadsgården, for uppför Mosebacke, in i trädgården och slog emot en vägg. I detsamma öppnades väggen av en piga, som just rivit bort klistringen på innanfönstren; ett förfärligt os av stekflott, ölskvättar, granris och sågspån störtade ut och fördes långt bort av vinden, som nu medan köksan drog in den friska luften genom näsan, passade på att gripa fönstervadden som var beströdd med paljetter och berberisbär och törnrosblad, och började en ringdans utefter gångarne, i vilken snart gråsparvarne och bofinkarne deltogo, då de sålunda sågo sina bosättningsbekymmer till stor del undanröjda.",
      },
      {
        title: "Doktor Glas",
        author: "Hjalmar Söderberg",
        pdfLink: "/pdfs/doktor-glas-inledningar.pdf",
        type: "Inledning",
        cover: "https://sportkuriren.se/wp-content/uploads/hjalmar.jpg",
        about:
          "“Doktor Glas” av Hjalmar Söderberg är en gripande roman om den plågade läkaren Tyko Glas, som i sin dagbok berättar om sina moraliska och känslomässiga dilemman. Romanen, utgiven 1905, utforskar teman som kärlek, ensamhet och rättvisa, samtidigt som den ställer frågor om rättfärdigandet av extrema handlingar. Söderbergs sparsmakade och psykologiskt skarpa stil gör boken till en av de mest älskade och omdiskuterade i svensk litteratur.",
        previewText: "I går gick han förbi här igen...",
        fullText:
          "Jag har aldrig sett en sådan sommar. Rötmånadshetta sedan midten af maj. Hela dagen står ett tjockt töcken af damm alldeles stilla öfver gatorna och torgen.\n\nFörst på kvällen lefver man upp en smula. Jag tog en aftonpromenad nyss, som jag gör nästan hvar dag efter mina sjukbesök, och de äro icke många nu på sommaren. Det kommer en sval, jämn luftström från öster, töcknet lyfter, seglar långsamt bort och blir till en lång slöja af rödt stoft bort i väster. Intet skrammel af arbetsåkdon mera, bara en droska då och då, och spårvagnen som ringer. Jag går gatan ner i sakta mak, träffar emellanåt en bekant och står och pratar en stund i ett gathörn. Men hvarför skall jag jämt möta pastor Gregorius? Jag kan inte se den mannen utan att jag kommer att tänka på en anekdot jag en gång i världen hörde om Schopenhauer. Den bistre filosofen satt en kväll i ett hörn i sitt kafé, ensam som vanligt; dörren öppnas och en person med ett osympatiskt utseende stiger in. Schopenhauer betraktar honom med ett ansikte, förvridet af vämjelse och fasa, rusar upp och börjar dunka mannen i hufvudet med sin käpp. Det var bara med anledning af hans utseende.\n\nNå, jag är nu ingen Schopenhauer; då jag såg prästen komma emot mig på långt håll, det var på Vasabron, stannade jag hastigt och ställde mig att se på utsikten med armarna stödda mot broräcket. Helgeandsholmens grå hus, den gamla badstuns söndriga götiska träarkitektur som speglades bruten i det rinnande vattnet, de stora gamla pilarna, som doppade sina blad i strömmen. Jag hoppades att prästen inte hade sett mig och att han inte heller skulle känna igen min ryggtafla, och jag hade redan i det närmaste glömt honom, då jag plötsligt fick se honom stå bredvid mig med armarna på broräcket liksom jag och hufvudet på sned – i alldeles samma attityd som för tjugu år sen i Jakobs kyrka, då jag satt i familjebänken vid min salig mors sida och för första gången såg denna förskräckliga fysionomi skjuta upp i predikstolen som en otäck svamp och klämma i med sitt abba, käre fader. Samma feta, grådaskiga ansikte, samma smutsgula polisonger, litet gråa nu kanske, och samma outgrundligt gemena blick bakom glasögonen. Omöjligt att komma undan, jag är ju hans läkare nu liksom många andras, och han kommer till mig ibland med sina krämpor.\n\n – Se god afton, herr pastor, hur står det till. \n– Inte bra, inte riktigt bra, hjärtat är dåligt, bultar ojämnt, stannar ibland om nätterna, tycker jag. \n– Det gläder mig, tänkte jag, du kan gärna dö, din gamla rackare, så slipper jag se dig mera. Du har för resten en ung och vacker hustru, som du förmodligen pinar lifvet ur, och när du dör gifter hon om sig och skaffar sig en mycket bättre man. Men högt sade jag: jaså, jaha, pastorn kanske kommer upp till mig en af dagarna, så får vi se hur det är fatt. Men han hade mycket mera att tala om, viktiga saker: det är ju en rent af onaturlig värme och det är dumt att det skall byggas ett stort riksdagshus på den lilla holmen där, och min hustru är för resten inte heller riktigt kry. Så gick han till sist, och jag fortsatte min väg. Jag kom in i gamla staden, uppför Storkyrkobrinken, in i gränderna. En kvaf skymning i de smala remnorna mellan husen, och besynnerliga skuggor utmed väggarna, skuggor som man aldrig ser till nere i våra kvarter.",
      },
      {
        title: "Nils Holgerssons underbara resa",
        author: "Selma Lagerlöf",
        pdfLink: "/pdfs/nils-holgersson-inledningar.pdf",
        type: "Inledning",
        cover: "https://sportkuriren.se/wp-content/uploads/selma.jpeg",
        about:
          "“Nils Holgerssons underbara resa genom Sverige” av Selma Lagerlöf är en magisk berättelse om pojken Nils, som förvandlas till en pyssling och får följa med en flock vildgäss på en fantastisk resa genom Sveriges landskap. Genom sina äventyr lär sig Nils om landets natur, historia och kultur, samtidigt som han utvecklas som människa. Den älskade boken skrevs ursprungligen som en läsebok för svenska skolor och har blivit en klassiker som inspirerat generationer.",
        previewText: "Det var en gång en pojke...",
        fullText:
          "Det var en gång en pojke. Han var så där en fjorton år gammal, lång och ranglig och linhårig. Inte stort dugde han till: han hade mest av allt lust att sova och äta, och därnäst tyckte han om att ställa till odygd. \n\nNu var det en söndagsmorgon, och pojkens föräldrar höllo på att göra sig i ordning för att gå i kyrkan. Pojken själv satt i skjortärmarna på bordskanten och tänkte på hur lyckligt det var, att både far och mor gingo sin väg, så att han skulle få rå sig själv under ett par timmar. »Nu kan jag då ta ner fars bössa och skjuta av ett skott, utan att någon behöver lägga sig i det,» sade han för sig själv.\n\nMen det var nästan, som om far skulle ha gissat sig till pojkens tankar, för just som han stod på tröskeln och var färdig att gå, stannade han och vände sig mot honom. \n\n»Eftersom du inte vill gå i kyrkan med mor och mig,» sade han, »så tycker jag, att du åtminstone kan läsa predikan hemma. Vill du lova, att du gör det? \n\n”Ja,» sade pojken, »det kan jag väl göra.» Och han tänkte förstås, att inte skulle han läsa mer, än han hade lust till. \nPojken tyckte, att han aldrig hade sett mor så fortfärdig. I ett nu var hon borta vid vägghyllan, tog ner Luthers postilla och lade den på bordet framme vid fönstret med dagens predikan uppslagen. Hon slog också upp i evangelieboken och lade den bredvid postillan. Sist drog hon fram till bordet den stora länstolen, som blev köpt på auktionen i Vemmenhögs prästgård förra året, och där eljest ingen annan än far fick sitta.\n\nPojken satt och tänkte, att mor gjorde sig alltför mycket besvär med den där uppdukningen, för han ämnade inte läsa mer än en eller annan sida. Men nu var det för andra gången alldeles så, som om far skulle ha kunnat se tvärsigenom honom. Han gick fram till pojken och sade med sträng röst: \n»Kom nu ihåg, att du läser ordentligt! För när vi kommer tillbaka, ska jag förhöra dig på varenda sida, och har du då hoppat över någon, så går det inte väl för dig.» \n»Predikan är fjorton och en halv sida,» sade mor liksom för att råga måttet. »Du får nog sätta dig att läsa genast, om du ska hinna igenom den.\n\nDärmed gingo de äntligen, och då pojken stod i dörren och såg efter dem, tyckte han, att han hade blivit fångad i en fälla. »Nu går de nog och lyckönskar sig till att ha hittat på det så bra, att jag måste sitta och hänga över predikan hela tiden, som de är borta,» tänkte han.",
      },
    ],
  },
  miljobeskrivningar: {
    title: "Miljöbeskrivningar",

    description: "Oförglömliga miljöbeskrivningar",
    image: "https://sportkuriren.se/wp-content/uploads/miljo.jpg",

    books: [
      {
        title: "Utvandrarna",
        author: "Vilhelm Moberg",
        pdfLink: "/pdfs/utvandrarna-miljo.pdf",
        cover: "https://sportkuriren.se/wp-content/uploads/moberg.jpg",
        type: "Miljöbeskrivning",
        about:
          "“Utvandrarna” av Vilhelm Moberg är en episk berättelse om Karl Oskar och Kristina, ett bondpar som lämnar sitt svåra liv i Småland på 1800-talet för att söka en bättre framtid i Amerika. Genom deras ögon skildras utvandrarnas strävan, hopp och svårigheter i en av Sveriges mest älskade romanserier, som speglar den stora emigrationens tid.",
        previewText: "Kristina stod i den låga dörren...",
        fullText:
          "Kristina stod i den låga dörren till sin stuga och såg ut över bygden, som låg tyst och stilla i sommarkvällen. Solen hade nyss gått ner bakom Släthults blånande skogsås, men dess sista strålar färgade ännu himlen i väster röd. Det var en kväll så lugn och vacker som hon mindes många från sin barndom här i Duvemåla. Luften var fylld av doften från blommande syren och äppelträd, och från den närbelägna älven hördes forsens dova brus.",
      },
      {
        title: "Jerusalem",
        author: "Selma Lagerlöf",
        pdfLink: "/pdfs/jerusalem-miljo.pdf",
        cover: "https://sportkuriren.se/wp-content/uploads/selma.jpeg",
        type: "Miljöbeskrivning",
        about:
          "“Jerusalem” av Selma Lagerlöf är en storslagen roman om en grupp människor från en svensk by som ger sig av till Palestina för att följa sin tro. Boken, baserad på verkliga händelser, väver samman religiös fanatism, kärlek och bygemenskap i en gripande berättelse som utforskar människors inre konflikter och längtan efter mening.",
        previewText: "Den stora Ingmarsgården låg...",
        fullText:
          "Den stora Ingmarsgården låg i en vid dalgång. Milslånga åsar, branta och tallbevuxna, omgärdade dalen på båda sidor. En älv bröt fram ur en trång bergsklyfta i norr, forsade skummande förbi gården och fortsatte sedan lugnt och stilla genom de vida slätterna i söder. Runtom gården låg åkerfält så stora, att de kunde ha tillhört en herrgård, och på våren, då de lågo nysådda, kunde man bättre än annars se hur välskött och ypperlig jorden var.",
      },
    ],
  },
  avslutningar: {
    title: "Avslutningar",
    image: "https://sportkuriren.se/wp-content/uploads/avslutning.jpg",
    description: "Minnesvärda avslutningar",
    type: "Avslutning",
    books: [
      {
        title: "Kallocain",
        author: "Karin Boye",
        pdfLink: "/pdfs/kallocain-avslutningar.pdf",
        cover: "https://sportkuriren.se/wp-content/uploads/boye.jpeg",
        about:
          "“Kallocain” av Karin Boye är en dystopisk roman som utforskar en totalitär framtidsstat där övervakning och kontroll genomsyrar allt. Genom kemisten Leo Kalls ögon får vi följa utvecklingen av sanningsdrogen Kallocain, som avslöjar människors innersta tankar. Boken är en stark kritik av makt och förlusten av individualitet.",

        previewText: "Och så slutar min berättelse...",
        fullText:
          "Och så slutar min berättelse där den började: i cell 57 i Kemistaden nummer 4. Jag tänker på Linda och barnen, på Leo Kall och det gamla livet, och det är som att tänka på en dröm. Kanske var det just en dröm, en mardröm om ett samhälle där människorna blivit maskiner, där kärleken dött och där misstron regerade. Men jag vet att det var verklighet, och därför måste jag berätta, måste varna dem som kommer efter.",
      },
      {
        title: "Dvärgen",
        author: "Pär Lagerkvist",
        pdfLink: "/pdfs/dvargen-avslutningar.pdf",
        type: "Avslutning",
        cover: "https://sportkuriren.se/wp-content/uploads/lagerkvist.jpg",
        about:
          "“Dvärgen” av Pär Lagerkvist är en psykologisk och mörk berättelse om en dvärg vid en renässanshov, vars cyniska och ondskefulla natur speglar mänsklighetens värsta sidor. Genom dvärgens föraktfulla blick dissekerar Lagerkvist maktens spel, krig och mänsklig moral i en tidlös och skrämmande roman.",

        previewText: "När skymningen föll...",
        fullText:
          "När skymningen föll över borgen den kvällen, visste jag att mitt verk var fullbordat. Jag stod på krönet av det högsta tornet och såg ut över staden som låg där nere, försänkt i mörker och tystnad. Facklorna som tändes en efter en var som små ljuspunkter i ett hav av skuggor, och jag log för mig själv, ty jag visste att ingen någonsin skulle glömma vad som hänt här idag. Mitt namn skulle leva kvar i historien, inte som en fotnot, utan som ett bevis på att även de minsta och mest föraktade kan förändra världen.",
      },
    ],
  },
};

const LiteratureSchool = ({ user, setMinimized }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const lessonTitleRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageInstructions, setImageInstructions] = useState("");
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [loadingImageIndex, setLoadingImageIndex] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const quizSectionRef = useRef(null);
  const exercisesSectionRef = useRef(null);
  const timelineSectionRef = useRef(null);

  // Basic settings
  const [lessonTheme, setLessonTheme] = useState("");
  const [language, setLanguage] = useState("svenska");
  const [grade, setGrade] = useState("åk9");
  const [numOfDiaryEntries, setNumOfDiaryEntries] = useState(5);
  const [diaryEntryLength, setDiaryEntryLength] = useState("Långa");

  // Generation states
  const [progress, setProgress] = useState(0);
  const [isGeneratingLesson, setIsGeneratingLesson] = useState(false);
  const [lessonData, setLessonData] = useState("");

  // Lit school:
  const [activeStep, setActiveStep] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [editedText, setEditedText] = useState("");

  // References for content
  const [customTheme, setCustomTheme] = useState("");
  const [shareDialogOpenFriends, setShareDialogOpenFriends] = useState(false);
  const [predefinedTheme, setPredefinedTheme] = useState(null);
  const subject = searchParams.get("subject") || null;
  const viewMode = searchParams.get("v") === "t";
  const additionalThemes = getThemes(subject);
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const styles = getStyles(theme);
  const [showControlPanel, setShowControlPanel] = useState(false);

  const transcriptRef = useRef("");
  const uploadedFileRef = useRef("");
  const bookContentRef = useRef("");
  const wikipediaContentRef = useRef("");
  const randomizedContentRef = useRef("");
  const coursePlanContentRef = useRef("");

  const quizButtonRef = useRef(null);
  const timelineButtonRef = useRef(null);
  const timelineSimpleButtonRef = useRef(null);
  const timelineSimpleSectionRef = useRef(null);
  const comprehensionButtonRef = useRef(null);
  const panelRef = useRef();
  const translateButtonRef = useRef(null);
  const shareLinkButtonRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Add click handlers for the DashedPaper components
  const handleQuizPaperClick = () => {
    quizButtonRef.current?.click();
  };

  const handleTimelineSimplePaperClick = () => {
    timelineSimpleButtonRef.current?.click();
  };

  const handleComprehensionPaperClick = () => {
    comprehensionButtonRef.current?.click();
  };

  const handleTimelinePaperClick = () => {
    timelineButtonRef.current?.click();
  };
  const { lessonId } = useParams();

  useEffect(() => {
    if (lessonData) {
      setMinimized(true);
    } else {
      setMinimized(false);
    }
  }, [lessonData]);

  useEffect(() => {
    Logger.log("lessonData: ", lessonData);
    const isDataValid =
      lessonData !== null &&
      lessonData !== undefined &&
      Object.keys(lessonData).length > 0 &&
      !Object.values(lessonData).includes("");

    if (isDataValid !== showControlPanel) {
      const timer = setTimeout(() => {
        setShowControlPanel(isDataValid);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [lessonData, showControlPanel]);

  useEffect(() => {
    const loadLessonFromId = async () => {
      if (lessonId) {
        try {
          const q = query(
            collection(db, "lesson_usage"),
            where("__name__", ">=", lessonId),
            where("__name__", "<=", lessonId + "\uf8ff"),
            where("lessonType", "==", "LiteratureSchool.js"),
            limit(1),
          );

          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const docData = querySnapshot.docs[0].data();

            if (!docData.hasOpened) {
              await updateDoc(querySnapshot.docs[0].ref, {
                hasOpened: true,
                openedAt: Date.now(),
              });
            }

            setLessonData(docData.data);

            // Scroll to lesson content
            // setTimeout(() => {
            //   lessonTitleRef.current?.scrollIntoView({
            //     behavior: "smooth",
            //     block: "start",
            //   });
            // }, 100);

            showSnackbar("Lektionen har laddats in.", "success");
          } else {
            showSnackbar("Kunde inte hitta lektionen", "error");
          }
        } catch (error) {
          console.error("Error loading lesson:", error);
          showSnackbar("Ett fel uppstod när lektionen skulle laddas", "error");
        }
      }
    };

    loadLessonFromId();
  }, lessonId);
  const handleShare = async (
    lessonComment,
    lessonName,
    selectedImage,
    tags,
    shareData,
  ) => {
    setIsSharing(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    // try {
    //   // Create a document for each student
    //   const sharePromises = shareData.studentIds.map((studentId) => {
    //     const documentData = {
    //       userId: user.uid,
    //       shared: true,
    //       sharedTo: studentId,
    //       comment: shareData.comment,
    //       sharedAt: Date.now(),
    //       fileUrl: shareData.fileUrl,
    //       lessonType: "LiteratureSchool.js",
    //       timestamp: serverTimestamp(),
    //       lessonComment,
    //       lessonName,
    //       coverImage: selectedImage,
    //       hasPrinted: true,
    //       data: lessonData,
    //     };
    //     Logger.log(
    //       "Adding document for student",
    //       studentId,
    //       ":",
    //       JSON.stringify(documentData, null, 2),
    //     );
    //     return addDoc(collection(db, "lesson_usage"), documentData);
    //   });
    //   await Promise.all(sharePromises);
    //   showSnackbar(`Du har nu delat lektionen!`, "success");
    // }

    try {
      // Create a document for each student and then create notifications with the doc IDs
      const sharePromises = shareData.studentIds.map(async (studentId) => {
        const documentData = {
          userId: user.uid,
          shared: true,
          sharedTo: studentId,
          comment: shareData.comment,
          sharedAt: Date.now(),
          fileUrl: shareData.fileUrl,
          lessonType: "LiteratureSchool.js", // Changed to match original
          timestamp: serverTimestamp(),
          lessonComment,
          lessonName,
          coverImage: selectedImage,
          hasPrinted: true,
          data: lessonData,
        };
        // Create the lesson document and get its ID
        const lessonDoc = await addDoc(
          collection(db, "lesson_usage"),
          documentData,
        );
        Logger.log(
          "Adding document for student",
          studentId,
          ":",
          JSON.stringify(documentData, null, 2),
        );
        // Create notification with the lesson doc ID
        await addDoc(collection(db, "notifications"), {
          type: "lesson_shared",
          recipientId: studentId,
          senderId: user.uid,
          read: false,
          createdAt: serverTimestamp(),
          lessonData: {
            lessonId: lessonDoc.id,
            lessonType: documentData.lessonType,
            lessonName: documentData.lessonName,
            fileUrl: documentData.fileUrl,
            comment: documentData.comment,
            coverImage: selectedImage,
          },
        });
        return lessonDoc;
      });
      await Promise.all(sharePromises);
      showSnackbar(`Du har nu delat lektionen!`, "success");
    } catch (error) {
      showSnackbar("Fel vid delande av lektionen: " + error.message, "error");
      console.error(error);
    } finally {
      setIsSharing(false);
    }
  };

  const handleSaveLessonClick = () => {
    setSaveDialogOpen(true);
  };

  const handleSaveWithComment = async (comment, name, coverImage, tags) => {
    await trackLesson(
      user.uid,
      "LiteratureSchool.js",
      lessonData,
      null,
      true,
      comment,
      name,
      coverImage,
      tags,
    );
    setDrawerOpen(true);
    showSnackbar("Lektionen sparad i din lektionsbank!", "success");
  };

  const toggleViewMode = () => {
    const currentParams = Object.fromEntries(searchParams.entries());
    if (currentParams.v === "t") {
      delete currentParams.v;
    } else {
      currentParams.v = "t";
    }
    setSearchParams(currentParams);
  };

  const simulateProgress = (startProgress, endProgress, duration) => {
    const stepSize = 0.5;
    const steps = (endProgress - startProgress) / stepSize;
    const stepDuration = duration / steps;
    let currentProgress = startProgress;

    const intervalId = setInterval(() => {
      currentProgress += stepSize;
      if (currentProgress >= endProgress) {
        clearInterval(intervalId);
      } else {
        setProgress(currentProgress);
      }
    }, stepDuration);

    return () => clearInterval(intervalId);
  };

  const handleGenerateLesson = async () => {
    setProgress(0);
    let stopProgressSimulation = null;
    setIsGeneratingLesson(true);
    const gradeInstructionsText = gradeInstructions[grade];
    setLessonData(null);

    try {
      let themeToUse;

      if (lessonTheme === "youtube") {
        themeToUse = transcriptRef.current;
      } else if (lessonTheme === "customTheme") {
        themeToUse = customTheme;
      } else if (lessonTheme === "predefined") {
        themeToUse = predefinedTheme;
      } else if (lessonTheme === "fileUpload") {
        themeToUse = uploadedFileRef.current;
      } else if (lessonTheme === "wikipedia") {
        themeToUse = wikipediaContentRef.current;
      } else if (lessonTheme === "randomizer") {
        themeToUse = randomizedContentRef.current;
      } else if (lessonTheme === "book") {
        themeToUse = bookContentRef.current;
      } else if (lessonTheme === "url") {
        themeToUse = urlContentRef.current;
      } else {
        themeToUse = coursePlanContentRef.current;
      }

      stopProgressSimulation = simulateProgress(0, 90, 15000);

      let response = await callApi(
        [
          {
            role: "system",
            content: `You are an expert historian and creative writer specializing in creating authentic-feeling diary entries in ${language}. 
            Create a series of fictitious diary entries that feel personal and engaging while maintaining historical accuracy. Follow the exact JSON structure provided in the prompt.`,
          },
          {
            role: "user",
            content: `
            - Theme: "${themeToUse}"
            - Grade: "${gradeInstructionsText}"
            - Amount of diary entries: Exactly ${numOfDiaryEntries} diary entries
            - Length of each diary entry: ${diaryEntryLength}
            - Language: ${language}


            RESPOND IN THIS EXACT JSON FORMAT:
            {
            }
            `,
          },
        ],
        6000,
        true,
        user.uid,
      );

      if (stopProgressSimulation) stopProgressSimulation();
      setProgress(95);

      let messageContent = response.data.choices[0].message.content.trim();
      if (messageContent.startsWith("```json")) {
        messageContent = messageContent.slice(7, -3).trim();
      } else if (messageContent.startsWith("```")) {
        messageContent = messageContent.slice(3, -3).trim();
      }

      const data = JSON.parse(messageContent);
      setProgress(100);
      setLessonData({
        ...data,
        diaryEntries: data.diaryEntries.map((entry) => ({
          ...entry,
          imageUrl: null,
        })),
      });

      Logger.log("response: ", JSON.stringify(lessonData, null, 2));

      await trackLesson(user.uid, "LiteratureSchool.js", null, {
        language,
        lessonTheme,
      });

      showSnackbar("Din lektion har genererats!", "success");
    } catch (error) {
      console.error("Error generating lesson:", error);
      showSnackbar(
        "Ett fel uppstod när lektionen skulle genereras. Försök igen!",
        "error",
      );
    } finally {
      setIsGeneratingLesson(false);
    }
  };

  const handleGenerateImages = async () => {
    if (!lessonData) return;
    setIsGeneratingImages(true);
    handleCloseModal();

    try {
      const imagePromises = lessonData.diaryEntries.map((entry) => {
        const prompt = `Create a photorealistic image depicting a scene from ${entry.date}, based on this: ${entry.content.slice(0, 200)}. ${imageInstructions}`;
        return generateAndProcessImage(prompt, user.uid);
      });

      const imageUrls = await Promise.all(imagePromises);

      setLessonData((prevData) => ({
        ...prevData,
        diaryEntries: prevData.diaryEntries.map((entry, index) => ({
          ...entry,
          imageUrl: imageUrls[index],
        })),
      }));

      showSnackbar("Bilderna har genererats!", "success");
    } catch (error) {
      handleImageGenerationError(error, true, showSnackbar);
    } finally {
      setIsGeneratingImages(false);
      setLoadingImageIndex(null);
    }
  };

  const handleGenerateSpecificImage = async (index) => {
    setIsGeneratingImages(true);
    setLoadingImageIndex(index);
    handleCloseModal();

    try {
      const prompt = `Create a photorealistic image depicting this: ${lessonData.about}. ${imageInstructions}`;

      const imageUrl = await generateAndProcessImage(prompt, user.uid);

      if (imageUrl) {
        setLessonData((prevData) => ({
          ...prevData,
          cover: imageUrl,
        }));
        showSnackbar("Bilden har genererats!", "success");
      }
    } catch (error) {
      handleImageGenerationError(error, false, showSnackbar);
    } finally {
      setIsGeneratingImages(false);
      setLoadingImageIndex(null);
    }
  };

  const handleImageButtonClick = (index = null) => {
    setSelectedImageIndex(index);
    setShowImageModal(true);
  };

  const handleCloseModal = () => {
    setShowImageModal(false);
    setImageInstructions("");
  };

  const handleGenerateWithInstructions = async () => {
    if (selectedImageIndex !== null) {
      handleCloseModal();
      await handleGenerateSpecificImage(selectedImageIndex);
    } else {
      handleCloseModal();
      await handleGenerateImages();
    }
  };

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          ...styles.innerContainer,
          width: { xs: "100%", md: "100%" },
          margin: "0 auto",
        }}
      >
        {!viewMode && (
          <>
            {!lessonData && (
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  ...styles.newH1,
                }}
              >
                Litteraturskolan
              </Typography>
            )}
          </>
        )}

        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          {lessonData && (
            <Button
              variant="purp"
              sx={{ ...styles.tightButton, display: "none" }}
              disableRipple
              onClick={() => {
                setTimeout(() => {
                  lessonTitleRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start", // This ensures it scrolls to the top of the element
                  });
                }, 100);
                toggleViewMode();
              }}
              startIcon={
                searchParams.get("v") === "t" ? (
                  <FullscreenExitIcon />
                ) : (
                  <FullscreenIcon />
                )
              }
            >
              {searchParams.get("v") === "t" ? "Redigeringsläge" : "Elevläge"}
            </Button>
          )}
          {!viewMode && (
            <>
              <Tooltip title="Dina sparade lektioner">
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  sx={{ ...styles.iconButtonBg }}
                >
                  <BookmarkBorderIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>

        <LessonDrawer
          userId={user.uid}
          lessonType={"LiteratureSchool.js"}
          theme={theme}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          lessonIcon={NewspaperIcon}
          onSelectLesson={(lesson) => {
            setLessonData(lesson.data);
            setDrawerOpen(false);
            // setTimeout(() => {
            //   lessonTitleRef.current?.scrollIntoView({
            //     behavior: "smooth",
            //     block: "start", // This ensures it scrolls to the top of the element
            //   });
            // }, 100);
            showSnackbar(`Lektionen har laddats in.`, "success");
          }}
        />

        {!viewMode && (
          <>
            {!lessonData && (
              <InfoBox
                icon={ClassIcon}
                title="Litteraturskolan"
                instructions={[
                  "Välj typ av text (inledning, miljöbeskrivning eller avslutning)",
                  "Välj från vilken bok detta ska hämtas",
                  "Lägg till valfria övningar och spara, skriv ut eller dela med vänner eller elever",
                ]}
                theme={theme}
                styles={styles}
                user={user}
                lessonType="LiteratureSchool.js"
              />
            )}
            <Box
              ref={lessonTitleRef}
              sx={{ ...styles.headlineLessonBox, mt: 4 }}
            >
              <Typography
                variant="h6"
                sx={{ ...styles.headlineLessonBoxTitle, display: "none" }}
              >
                Generera din egen litteraturlektion...
              </Typography>
            </Box>
            <Box
              sx={{
                ...styles.freshBox,
                padding: "0px",
                background: "none",
                border: "none",
                display: lessonData ? "none" : "block",
                mt: 4,
              }}
            >
              <Stepper
                activeStep={activeStep}
                sx={{
                  width: { xs: "100%", md: "50%" },
                  margin: "0 auto",
                  mb: 6,
                }}
              >
                {[
                  "Välj typ av text",
                  "Välj från vilken bok",
                  "Bekräfta val",
                ].map((label, index) => (
                  <Step
                    key={index}
                    sx={{
                      cursor: index < activeStep ? "pointer" : "default",
                      "&:hover":
                        index < activeStep
                          ? {
                              "& .MuiStepLabel-label": {
                                color: "primary.main",
                              },
                              "& .MuiStepIcon-root": {
                                color: "primary.main",
                              },
                            }
                          : {},
                    }}
                    onClick={() => {
                      setLessonData(null);
                      if (index < activeStep) {
                        setActiveStep(index);
                      }
                    }}
                  >
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              {activeStep === 0 && (
                <Grid container spacing={3}>
                  {Object.entries(LESSON_DATA).map(([key, data]) => (
                    <Grid item xs={12} md={4} key={key}>
                      {data.title === "Miljöbeskrivningar" ||
                      data.title === "Avslutningar" ? (
                        <Box sx={{ position: "relative" }}>
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backdropFilter: "blur(2px)",

                              zIndex: 2,
                              borderRadius: "12px",
                            }}
                          />
                          <LessonCard
                            name={data.title}
                            imageUrl={data.image}
                            height={500}
                            width
                            centered={true}
                            comment={data.description}
                            onClick={() => {
                              setSelectedType(key);
                              setActiveStep(1);
                            }}
                            selected={selectedType === key}
                          />
                        </Box>
                      ) : (
                        <LessonCard
                          name={data.title}
                          imageUrl={data.image}
                          height={500}
                          width
                          centered={true}
                          comment={data.description}
                          onClick={() => {
                            setSelectedType(key);
                            setActiveStep(1);
                          }}
                          selected={selectedType === key}
                        />
                      )}
                    </Grid>
                  ))}
                </Grid>
              )}

              {activeStep === 1 && selectedType && (
                <Grid container spacing={5}>
                  {LESSON_DATA[selectedType].books.map((book, index) => (
                    <Grid item xs={12} md={4} key={index}>
                      <LessonCard
                        name={book.title}
                        centered={true}
                        height={500}
                        imageUrl={book.cover}
                        onClick={() => {
                          setSelectedBook(book);
                          setEditedText(book.fullText); // Initialize edited text with fullText
                          // setLessonData(book);
                          setActiveStep(2); // Proceed to step 3
                        }}
                        selected={selectedBook?.title === book.title}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}

              {activeStep === 2 && selectedBook && (
                <Box sx={{ width: "100%", textAlign: "center" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      gap: 4,
                      width: { xs: "100%", md: "70%" },
                      maxWidth: "xl",
                      mx: "auto",
                    }}
                  >
                    {/* Left column - Text content */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontWeight: 700,
                          mb: 2,
                          fontSize: "1.4rem",
                          lineHeight: 1.2,
                          textAlign: "left",
                        }}
                      >
                        {selectedBook.title}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontWeight: 500,
                          mb: 2,
                          fontSize: "1rem",
                          lineHeight: 1.5,
                          textAlign: "left",
                        }}
                      >
                        {selectedBook.about}
                      </Typography>
                    </Box>

                    {/* Right column - Image */}
                    <Box sx={{ flex: 1 }}>
                      <img
                        src={selectedBook.cover}
                        alt={selectedBook.title}
                        width="200px"
                        style={{ borderRadius: 10 }}
                      />
                    </Box>
                  </Box>

                  <Button
                    variant="purp"
                    onClick={() => {
                      setLessonData(selectedBook);
                      setActiveStep(0);
                      setTimeout(() => {
                        lessonTitleRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }, 100);
                    }}
                    sx={{ mt: 2 }}
                    endIcon={<CheckCircleOutlineIcon />}
                  >
                    Bekräfta — gå vidare
                  </Button>
                </Box>
              )}
            </Box>

            {activeStep === 0 && (
              <>
                <Box sx={{ display: "none" }}>
                  <Box sx={{ ...styles.headlineLessonBox, mt: 10 }}>
                    <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                      ...eller välj en färdig lektion
                    </Typography>
                  </Box>
                  <Box sx={{ mt: -6 }}>
                    <FeaturedLessons
                      theme={theme}
                      styles={styles}
                      onlySelectedLessonType="LiteratureSchool.js"
                    />
                  </Box>
                </Box>
              </>
            )}
            <Box sx={{ display: "none" }}>
              <Box sx={styles.headlineLessonBox}>
                <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                  2. Generera lektionen
                </Typography>
              </Box>

              <Box sx={{ ...styles.freshBox, padding: "40px" }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "500", mb: 1 }}
                    >
                      Årskurs:
                    </Typography>
                    <GradeSelector grade={grade} setGrade={setGrade} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "500", mb: 1 }}
                    >
                      Språk:
                    </Typography>
                    <LanguageSelector
                      language={language}
                      setLanguage={setLanguage}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <Typography
                        variant="formLabelSinUnderline"
                        sx={{ fontWeight: "500", mt: 1 }}
                      >
                        Antal dagboksinlägg
                      </Typography>
                      <Select
                        labelId="num-questions-label"
                        id="num-questions-select"
                        value={numOfDiaryEntries}
                        sx={{ mt: 1 }}
                        onChange={(e) => setNumOfDiaryEntries(e.target.value)}
                      >
                        <MenuItem value={2} selected>
                          2
                        </MenuItem>
                        <MenuItem value={5}>5</MenuItem>
                        {user.admin && <MenuItem value={10}>10</MenuItem>}
                        <Tooltip title="Endast tillgängligt för premium" arrow>
                          <span>
                            <MenuItem value={15} disabled>
                              15
                            </MenuItem>
                            <MenuItem value={20} disabled>
                              20
                            </MenuItem>
                            <MenuItem value={30} disabled>
                              30
                            </MenuItem>
                            <MenuItem value={40} disabled>
                              40
                            </MenuItem>
                            <MenuItem value={50} disabled>
                              50
                            </MenuItem>
                          </span>
                        </Tooltip>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <Typography
                        variant="formLabelSinUnderline"
                        sx={{ fontWeight: "500" }}
                      >
                        Längd på dagboksinläggen
                      </Typography>
                      <Select
                        sx={{ mt: 1 }}
                        value={diaryEntryLength}
                        onChange={(e) => setDiaryEntryLength(e.target.value)}
                      >
                        <MenuItem value="Korta">Korta</MenuItem>
                        <MenuItem value="Medellånga">Medellånga</MenuItem>
                        <MenuItem value="Långa">Långa</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "12px",
                    width: "100%",
                  }}
                >
                  <Button
                    variant={
                      theme.palette.mode === "light" ? "purp" : "contained"
                    }
                    color="primary"
                    onClick={handleGenerateLesson}
                    disabled={isGeneratingLesson}
                    sx={{ mt: 7 }}
                    startIcon={
                      !isGeneratingLesson ? (
                        <GenerateIcon style={{ color: "#fff" }} />
                      ) : (
                        <CircularProgress size={16} style={{ color: "#fff" }} />
                      )
                    }
                  >
                    {isGeneratingLesson ? "Genererar..." : "Generera lektion"}
                  </Button>
                </div>
                {isGeneratingLesson && (
                  <Box sx={{ mt: 6 }}>
                    <ProgressBar
                      progress={progress}
                      label="Genererar lektion — tar 30-60 sekunder..."
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}

        {lessonData && (
          <>
            <BackButton
              currentStep={3}
              onClick={() => {
                setLessonData(null);
              }}
              show={lessonData}
            />

            {!viewMode && (
              <Box
                ref={lessonTitleRef}
                sx={{ ...styles.headlineLessonBox, mt: 0, display: "none" }}
              >
                <Typography
                  variant="h6"
                  sx={{ ...styles.headlineLessonBoxTitle, display: "none" }}
                >
                  3. Din litteraturlektion
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexDirection: {
                  xs: "column",
                  md: "row",
                },
              }}
            >
              <Box
                sx={{
                  ...styles.freshBox,
                  padding: "30px",

                  width: {
                    xs: "100%", // Full width on mobile (extra small devices)
                    sm: viewMode ? "70%" : undefined, // Original width from sm breakpoint up
                  },

                  ...(viewMode ? { flex: "0 0 70%" } : { flex: "1 1 auto" }),

                  margin: viewMode ? "0 auto" : undefined,
                  marginTop: viewMode ? "100px" : undefined,
                }}
              >
                <Box
                  sx={{
                    position: "relative",

                    minHeight: "100%",
                    overflow: "visible",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      gap: 4,
                      width: "100%",
                      maxWidth: "xl",
                      mx: "auto",
                      p: 0,
                      mb: 4,
                    }}
                  >
                    {/* Left column - Text content */}
                    <Box sx={{ flex: 1 }}>
                      {isEditMode ? (
                        <TextField
                          fullWidth
                          multiline
                          value={lessonData.title}
                          onChange={(e) =>
                            setLessonData((prev) => ({
                              ...prev,
                              title: e.target.value,
                            }))
                          }
                          sx={{
                            "& .MuiInputBase-input": {
                              fontFamily: "Georgia, serif",
                              fontWeight: 700,
                              fontSize: "2.5rem",
                              lineHeight: 1.2,
                            },
                          }}
                        />
                      ) : (
                        <Typography
                          variant="h3"
                          sx={{
                            fontFamily: "Georgia, serif",
                            fontWeight: 700,
                            mb: 2,
                            fontSize: "2.5rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {lessonData.title}
                        </Typography>
                      )}

                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontWeight: 600,
                          mt: 5,
                          fontSize: "1.4rem",
                          lineHeight: 1.5,
                        }}
                      >
                        Om boken
                      </Typography>

                      {isEditMode ? (
                        <TextField
                          fullWidth
                          multiline
                          value={lessonData.about}
                          onChange={(e) =>
                            setLessonData((prev) => ({
                              ...prev,
                              about: e.target.value,
                            }))
                          }
                          sx={{
                            mt: 2,
                            "& .MuiInputBase-input": {
                              fontFamily: "Georgia, serif",
                              fontWeight: 500,
                              fontSize: "1.1rem",
                              lineHeight: 1.7,
                              color: "text.secondary",
                            },
                          }}
                        />
                      ) : (
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontFamily: "Georgia, serif",
                            fontWeight: 500,
                            mt: 2,
                            fontSize: "1.1rem",
                            lineHeight: 1.7,
                            color: "text.secondary",
                          }}
                        >
                          {lessonData.about}
                        </Typography>
                      )}
                    </Box>

                    {/* Right column - Image */}
                    <Box sx={{ flex: 1 }}>
                      <HoverableImage
                        imageUrl={lessonData.cover}
                        onGenerateClick={() => handleImageButtonClick(0)}
                        isLoading={isGeneratingImages}
                        loadingImageIndex={loadingImageIndex}
                        currentIndex={0}
                        userId={user.uid}
                        onImageUpdate={(url) =>
                          setLessonData((prev) => ({ ...prev, cover: url }))
                        }
                        theme={theme}
                        showSnackbar={showSnackbar}
                      />
                    </Box>
                  </Box>

                  {isEditMode ? (
                    <TextField
                      fullWidth
                      multiline
                      value={lessonData.type}
                      onChange={(e) =>
                        setLessonData((prev) => ({
                          ...prev,
                          type: e.target.value,
                        }))
                      }
                      sx={{
                        mt: 10,
                        mb: 2,
                        "& .MuiInputBase-input": {
                          fontFamily: "Georgia, serif",
                          fontWeight: 700,
                          fontSize: "2.5rem",
                          lineHeight: 1.2,
                        },
                      }}
                    />
                  ) : (
                    <Typography
                      variant="h3"
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontWeight: 700,
                        mb: 2,
                        mt: 10,
                        fontSize: "2.5rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {lessonData.type}
                    </Typography>
                  )}

                  {isEditMode ? (
                    <TextField
                      fullWidth
                      multiline
                      value={lessonData.fullText}
                      onChange={(e) =>
                        setLessonData((prev) => ({
                          ...prev,
                          fullText: e.target.value,
                        }))
                      }
                      sx={{
                        mb: 3,
                        "& .MuiInputBase-input": {
                          fontFamily: "Georgia, serif",
                          fontSize: "1.2rem",
                          lineHeight: 1.8,
                          whiteSpace: "pre-wrap",
                        },
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: "1.2rem",
                        lineHeight: 1.8,
                        mb: 3,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {lessonData.fullText}
                    </Typography>
                  )}

                  {lessonData?.exercises && (
                    <Box ref={exercisesSectionRef}>
                      <LessonExercisesDisplay
                        exercises={lessonData.exercises}
                        onUpdateLessonData={setLessonData}
                        viewMode={viewMode}
                        theme={theme}
                        styles={styles}
                      />
                    </Box>
                  )}

                  {lessonData?.quiz && (
                    <Box
                      ref={quizSectionRef}
                      sx={{ mt: 5, mb: 5, position: "relative" }}
                    >
                      <LessonQuizDisplay
                        quizData={lessonData.quiz}
                        theme={theme}
                        styles={styles}
                        userId={user.uid}
                        viewMode={viewMode}
                        setLessonData={setLessonData}
                      />
                    </Box>
                  )}

                  {lessonData?.timelinesSimple && (
                    <Box
                      ref={timelineSimpleSectionRef}
                      sx={{ mt: 5, mb: 5, position: "relative" }}
                    >
                      <TimelineSimpleDisplay
                        timelineData={lessonData.timelinesSimple}
                        onTimelineDataChange={(newData) =>
                          setLessonData({
                            ...lessonData,
                            timelinesSimple: newData,
                          })
                        }
                        theme={theme}
                        styles={styles}
                        viewMode={viewMode}
                        user={user}
                      />
                    </Box>
                  )}

                  {lessonData?.timelines && (
                    <Box
                      ref={timelineSectionRef}
                      sx={{ mt: 5, mb: 5, position: "relative" }}
                    >
                      <TimelineDisplay
                        timelineData={lessonData.timelines}
                        onTimelineDataChange={(newData) =>
                          setLessonData({ ...lessonData, timelines: newData })
                        }
                        theme={theme}
                        styles={styles}
                        withImages={true}
                        viewMode={viewMode}
                        user={user}
                        onDelete={
                          viewMode
                            ? undefined
                            : (indexToDelete) => {
                                setLessonData((prev) => ({
                                  ...prev,
                                  timelines: prev.timelines.filter(
                                    (_, index) => index !== indexToDelete,
                                  ),
                                }));
                                showSnackbar(
                                  "Tidslinjen har tagits bort",
                                  "success",
                                );
                              }
                        }
                      />
                    </Box>
                  )}

                  {!viewMode && (
                    <DashedPapers
                      handleQuizPaperClick={handleQuizPaperClick}
                      handleComprehensionPaperClick={
                        handleComprehensionPaperClick
                      }
                      handleTimelinePaperClick={handleTimelinePaperClick}
                      handleTimelineSimplePaperClick={
                        handleTimelineSimplePaperClick
                      }
                      quizSectionRef={quizSectionRef}
                      timelineSectionRef={timelineSectionRef}
                      timelineSimpleSectionRef={timelineSimpleSectionRef}
                      exercisesSectionRef={exercisesSectionRef}
                      lessonData={lessonData}
                      setLessonData={setLessonData}
                    />
                  )}
                </Box>
              </Box>

              {!viewMode && (
                <Box sx={{ position: "relative" }}>
                  <Box
                    sx={{
                      ...styles.lessonControlPanelNuevo2,

                      "& .MuiButton-root": {
                        ...styles.lessonControlPanelNewButtons2,
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                      }}
                    >
                      Hantera
                    </Typography>

                    <Button
                      variant="success"
                      sx={{
                        mt: 2,
                        ml: 2,

                        ...(isEditMode && {
                          color: "green !important",
                          "&.MuiButton-root": {
                            fontWeight: "700 !important",
                          },
                        }),
                      }}
                      onClick={() => setIsEditMode(!isEditMode)}
                      startIcon={isEditMode ? <SaveAsIcon /> : <EditIcon />}
                    >
                      {isEditMode ? "Spara ändringar" : "Redigera"}
                    </Button>

                    <Translate
                      buttonRef={translateButtonRef}
                      lessonData={lessonData}
                      onTranslated={setLessonData}
                      callApi={callApi}
                      user={user}
                      showSnackbar={showSnackbar}
                    />

                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        mt: 3,
                      }}
                    >
                      Spara
                    </Typography>
                    <Button
                      variant="print"
                      startIcon={<SaveAsIcon />}
                      sx={{ mt: 2, ml: 2 }}
                      onClick={handleSaveLessonClick}
                    >
                      Spara
                    </Button>
                    <Button
                      variant="print"
                      startIcon={<PrintIcon />}
                      sx={{ mt: 2, ml: 2 }}
                      onClick={() => handlePrintLiteratureLesson(lessonData)}
                    >
                      Skriv ut
                    </Button>

                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        mt: 3,
                      }}
                    >
                      Dela
                    </Typography>
                    <ShareLink
                      buttonRef={shareLinkButtonRef}
                      key={lessonId}
                      lessonId={lessonId}
                      lessonData={lessonData}
                      user={user}
                      lessonType={"LiteratureSchool.js"}
                      onCopy={(message, severity) =>
                        showSnackbar(message, severity)
                      }
                    />

                    <Button
                      variant="success"
                      sx={{
                        mt: 2,
                        ml: 2,
                      }}
                      onClick={() => setShareDialogOpen(true)}
                      startIcon={<ShareIcon />}
                    >
                      Dela med elever
                    </Button>

                    <Button
                      variant="success"
                      sx={{}}
                      onClick={() => setShareDialogOpenFriends(true)}
                      startIcon={<ShareIcon />}
                    >
                      Dela med vänner
                    </Button>

                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        mt: 3,
                      }}
                    >
                      Visa
                    </Typography>
                    <Tooltip
                      title="Hur lektionen ser ut för eleverna"
                      placement="top"
                    >
                      <Button
                        variant="success"
                        onClick={() => {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                          toggleViewMode();
                        }}
                        sx={{ ml: 2, mt: 2 }}
                        startIcon={
                          searchParams.get("v") === "t" ? (
                            <FullscreenExitIcon />
                          ) : (
                            <FullscreenIcon />
                          )
                        }
                      >
                        {searchParams.get("v") === "t"
                          ? "Redigeringsläge"
                          : "Elevläge"}
                      </Button>
                    </Tooltip>

                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        mt: 3,
                      }}
                    >
                      Addera
                    </Typography>
                    <Box sx={{ display: "inline-block" }}>
                      <AddonQuiz
                        buttonRef={quizButtonRef}
                        lessonData={lessonData}
                        title={lessonData.title}
                        theme={theme}
                        user={user}
                        styles={styles}
                        showSnackbar={showSnackbar}
                        addQuizToLessonObject={(updatedData) => {
                          setLessonData(updatedData);
                          setTimeout(
                            () =>
                              quizSectionRef.current?.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              }),
                            100,
                          );
                        }}
                        buttonText="Lägg till  quiz"
                      />
                    </Box>
                    <Box sx={{ display: "inline-block" }}>
                      <ReadingComprehensionDialog
                        buttonRef={comprehensionButtonRef}
                        key={lessonData?.title}
                        lessonData={lessonData}
                        user={user}
                        theme={theme}
                        styles={styles}
                        buttonText="Lägg till övningar"
                        addExercisesToLessonObject={(updateFunction) => {
                          setLessonData((prevLessonData) => ({
                            ...prevLessonData,
                            exercises: [
                              ...(prevLessonData.exercises || []),
                              ...updateFunction(prevLessonData).exercises,
                            ],
                          }));
                          setTimeout(
                            () =>
                              exercisesSectionRef.current?.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              }),
                            100,
                          );
                        }}
                      />
                    </Box>

                    <Box sx={{ display: "none" }}>
                      <AddonTimeline
                        buttonRef={timelineButtonRef}
                        lessonData={lessonData}
                        theme={theme}
                        user={user}
                        styles={styles}
                        showSnackbar={showSnackbar}
                        addTimelineToLessonObject={(updatedData) => {
                          setLessonData(updatedData);
                          setTimeout(
                            () =>
                              timelineSectionRef.current?.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              }),
                            100,
                          );
                        }}
                        buttonText="Lägg till tidslinje"
                      />
                    </Box>
                    <Box sx={{ display: "inline-block" }}>
                      <AddonTimelineSimple
                        buttonRef={timelineSimpleButtonRef}
                        lessonData={lessonData}
                        theme={theme}
                        user={user}
                        styles={styles}
                        showSnackbar={showSnackbar}
                        addTimelineToLessonObject={(updatedData) => {
                          setLessonData(updatedData);
                          setTimeout(
                            () =>
                              timelineSimpleSectionRef.current?.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              }),
                            100,
                          );
                        }}
                        buttonText="Lägg till  tidslinje"
                      />

                      <Box>
                        <MoreToCome user={user} />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
            <SlideInPaper
              isExpanded={isExpanded}
              onExpandedChange={setIsExpanded}
              lessonTitleRef={lessonTitleRef}
              viewMode={viewMode}
              toggleViewMode={toggleViewMode}
              theme={theme}
              open={showControlPanel}
              user={user}
              onPrint={() => handlePrintLiteratureLesson(lessonData)}
              onTranslate={() => translateButtonRef.current?.click()}
              onShareLink={() => shareLinkButtonRef.current?.click()}
              onShareWithStudents={() => setShareDialogOpen(true)}
              onShareWithFriends={() => setShareDialogOpenFriends(true)}
              onSaveToBank={() => setSaveDialogOpen(true)}
              onStudentMode={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
                toggleViewMode();
              }}
              onGenerateExercises={() => {
                comprehensionButtonRef.current?.click();
              }}
              onGenerateQuiz={() => {
                quizButtonRef.current?.click();
              }}
              onGenerateAssignments={() => {
                comprehensionButtonRef.current?.click();
              }}
              onGenerateTimeline={() => {
                timelineButtonRef.current?.click();
              }}
            />

            <ShareDialog
              open={shareDialogOpen}
              onClose={() => setShareDialogOpen(false)}
              onShare={handleShare}
              user={user}
              isLessonSharing={true}
              theme={theme}
              lessonName={lessonData?.title || ""}
              lessonObject={lessonData}
              lessonType="LiteratureSchool.js"
            />
            <ShareDialog
              open={shareDialogOpenFriends}
              onClose={() => setShareDialogOpenFriends(false)}
              onShare={handleShare}
              user={user}
              isLessonSharing={true}
              theme={theme}
              lessonName={lessonData?.title || ""}
              lessonObject={lessonData}
              lessonType="LiteratureSchool.js"
              shareWithFriends={true}
            />
            <SaveLessonDialog
              key={lessonData?.title || ""}
              open={saveDialogOpen}
              onClose={() => setSaveDialogOpen(false)}
              onSave={handleSaveWithComment}
              theme={theme}
              lessonName={lessonData?.title || ""}
              lessonObject={lessonData}
              lessonType="LiteratureSchool.js"
            />
            <ImageGenerationModal
              open={showImageModal}
              onClose={handleCloseModal}
              onGenerate={handleGenerateWithInstructions}
              isGenerating={isGeneratingImages}
              instructions={imageInstructions}
              setInstructions={setImageInstructions}
              theme={theme}
            />

            <ImageLoadingDialog
              open={isGeneratingImages}
              message="Genererar bilder... Tar 30-60 sekunder."
            />

            <LoadingBackdrop
              isOpen={isSharing}
              imagePath="/img/AI.png"
              loadingText={"Delar lektionen..."}
            />
          </>
        )}

        <WelcomeDialogLessons
          key={lessonData}
          lessonType="LiteratureSchool.js"
          open={lessonData}
          title="Välkommen till Litteraturskolan!"
          imageSrc="https://sportkuriren.se/wp-content/uploads/books.png"
          imageSrcMaxWidth="50%"
          items={[
            {
              icon: "Edit",
              text: "Klicka på Redigera för att göra önskade ändringar i texten",
              color: "#ff9e7d",
            },

            {
              icon: "AutoAwesome",
              text: "Lägg till övningar eller quiz till lektionen vars innehåll baseras på texten",
              color: "#ff6b6b",
            },
            {
              icon: "Share",
              text: "Dela lektionen via en länk som elever (inget elevkonto krävs) kan gå in på  eller via Studera.AI (elevkonto krävs)",

              color: "#ff6b6b",
            },
            // {
            //   icon: "Image",
            //   text: "Generera en passande bild eller ladda upp en egen ",
            //   color: "#ff6b6b",
            // },

            {
              icon: "Print",
              text: "Du kan skriva ut lektionen om du inte vill dela den genom Studera.AI",
              color: "#ff6b6b",
            },
            {
              icon: "Translate",
              text: "Översätt lektionen till valfritt språk om du har elever som inte behärskar svenska fullt ut ännu",
              color: "#ff6b6b",
            },
            {
              icon: "SaveAs",
              text: "Glöm inte att spara lektionen i din lektionsbank så du när som helst kan gå tillbaka och redigera",
              color: "#ff9e7d",
            },
          ]}
        />
      </Box>
    </Box>
  );
};

export default LiteratureSchool;
